<template>
  <v-container>
    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <h1 class="mb-0">Gruppenanmeldungen</h1>
      <v-btn outlined color="primary" class="mt-2 mt-sm-0" @click="downloadCsv">
        <v-icon left small>mdi-download</v-icon>
        CSV exportieren
      </v-btn>
    </div>
    
    <v-card class="mb-4">
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h5">{{ totalShirts }}</div>
                <div class="text-caption">T-Shirts</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h5">{{ totalPayment }} €</div>
                <div class="text-caption">Erwartete Zahlung</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="bulkRegistrations"
      :expanded.sync="expanded"
      item-key="id"
      show-expand
      single-expand
      @click:row="toggleExpand"
      class="elevation-1"
    >
      <template v-slot:item.participantCount="{ item }">
        <v-chip small @click.stop="showParticipants(item)">
          {{ item.participantCount }}
        </v-chip>
      </template>

      <template v-slot:item.shirtCount="{ item }">
        <v-chip small @click.stop="showShirts(item)">
          {{ item.shirtCount }}
        </v-chip>
      </template>

      <template v-slot:item.totalPayment="{ item }">
        {{ item.totalPayment }} €
      </template>

      <template v-slot:item.hasPayed="{ item }">
        <v-chip :color="item.hasPayed ? 'green' : 'red'" dark small>
          {{ item.hasPayed ? 'Bezahlt' : 'Offen' }}
        </v-chip>
      </template>

      <template v-slot:item.paymentToken="{ item }">
        <code>{{ item.paymentToken }}</code>
      </template>

      <template v-slot:item.selfServiceLink="{ item }">
        <span @click.stop>
          <v-btn
            v-if="item.selfServiceToken"
            small
            outlined
            color="primary"
            :href="selfServiceUrl(item)"
            target="_blank"
            rel="noopener noreferrer"
          >
            Startnummern-Seite
          </v-btn>
          <span v-else class="text--disabled">—</span>
        </span>
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-0">
          <div class="pa-4 grey lighten-4">
            <strong>Adresse:</strong><br>
            <span v-if="item.companyName">{{ item.companyName }}<br></span>
            {{ item.addressFirstname }} {{ item.addressLastname }}<br>
            {{ item.street }} {{ item.streetNumber }}<br>
            <span v-if="item.addressExtra">{{ item.addressExtra }}<br></span>
            {{ item.plz }} {{ item.city }}<br>
            {{ item.country }}
            <div v-if="!item.hasPayed" class="mt-3">
              <v-btn color="primary" @click.stop="markAsPayed(item)">
                Als bezahlt markieren
              </v-btn>
            </div>
          </div>
        </td>
      </template>
    </v-data-table>

    <!-- Participants Dialog -->
    <v-dialog v-model="participantsDialog" max-width="600">
      <v-card>
        <v-card-title>Teilnehmer*innen - {{ selectedBulk?.team }}</v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Startnummer</th>
                <th>Name</th>
                <th>Bezahlt</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in selectedBulk?.participants" :key="p.id">
                <td>{{ p.startNumber }}</td>
                <td>{{ p.firstName }} {{ p.lastName }}</td>
                <td>
                  <v-icon :color="p.hasPayed ? 'green' : 'red'" small>
                    {{ p.hasPayed ? 'mdi-check' : 'mdi-close' }}
                  </v-icon>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="participantsDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Shirts Dialog -->
    <v-dialog v-model="shirtsDialog" max-width="500">
      <v-card>
        <v-card-title>T-Shirts - {{ selectedBulk?.team }}</v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Modell</th>
                <th>Größe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in selectedBulk?.shirts" :key="s.id">
                <td>{{ s.model }}</td>
                <td>{{ s.size }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="shirtsDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: "BulkRegistrationList",
  data: () => ({
    bulkRegistrations: [],
    participantsDialog: false,
    shirtsDialog: false,
    selectedBulk: null,
    expanded: [],
    headers: [
      { text: '', value: 'data-table-expand' },
      { text: 'Team', value: 'team' },
      { text: 'Kontakt E-Mail', value: 'contactEmail' },
      { text: 'Teilnehmer*innen', value: 'participantCount' },
      { text: 'T-Shirts', value: 'shirtCount' },
      { text: 'Erwartete Zahlung', value: 'totalPayment' },
      { text: 'Bezahlstatus', value: 'hasPayed' },
      { text: 'Token', value: 'paymentToken' },
      { text: 'Link Startnummern', value: 'selfServiceLink', sortable: false },
      { text: 'Erstellt', value: 'createdAt' }
    ]
  }),
  computed: {
    totalGroups() {
      return this.bulkRegistrations.length;
    },
    totalParticipants() {
      return this.bulkRegistrations.reduce((sum, b) => sum + b.participantCount, 0);
    },
    totalShirts() {
      return this.bulkRegistrations.reduce((sum, b) => sum + b.shirtCount, 0);
    },
    totalPayment() {
      return this.bulkRegistrations.reduce((sum, b) => sum + (b.totalPayment || 0), 0);
    }
  },
  mounted() {
    this.getBulkRegistrations();
  },
  methods: {
    downloadCsv() {
      const url = `${this.$base_url}/bulkregistration/csv`;
      const token = localStorage.pace_token;
      const requestConfig = {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      };
      axios.get(url, requestConfig)
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', 'gruppenanmeldungen.csv');
          document.body.appendChild(fileLink);
          fileLink.click();
          fileLink.remove();
          window.URL.revokeObjectURL(fileURL);
        })
        .catch((error) => {
          console.error('Error downloading bulk registration CSV:', error);
        });
    },
    getBulkRegistrations() {
      const url = `${this.$base_url}/bulkregistration/`;
      const token = localStorage.pace_token;
      const requestConfig = {
        headers: { Authorization: `Bearer ${token}` }
      };
      axios.get(url, requestConfig)
        .then((response) => {
          this.bulkRegistrations = response.data;
        })
        .catch((error) => {
          console.error('Error loading bulk registrations:', error);
        });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    showParticipants(bulk) {
      this.selectedBulk = bulk;
      this.participantsDialog = true;
    },
    showShirts(bulk) {
      this.selectedBulk = bulk;
      this.shirtsDialog = true;
    },
    markAsPayed(bulk) {
      const url = `${this.$base_url}/bulkregistration/markPayed/${bulk.id}`;
      const token = localStorage.pace_token;
      const requestConfig = {
        headers: { Authorization: `Bearer ${token}` }
      };
      axios.post(url, {}, requestConfig)
        .then(() => {
          this.getBulkRegistrations();
        })
        .catch((error) => {
          console.error('Error marking as paid:', error);
        });
    },
    toggleExpand(item) {
      const index = this.expanded.indexOf(item);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded = [item];
      }
    },
    selfServiceUrl(item) {
      if (!item.selfServiceToken) return '#';
      return `${window.location.origin}/#/bulk-me?token=${item.selfServiceToken}`;
    }
  }
};
</script>

<style scoped>
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
}
</style>
